import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { GalleryMedia } from "../api/contentful";
import {
  getGalleryLightboxSrc,
  preloadGalleryNeighbors,
} from "../utils/galleryImagePreload";
import "./GalleryLightbox.css";

const SWIPE_THRESHOLD_PX = 40;

type GalleryLightboxProps = {
  isOpen: boolean;
  media: GalleryMedia[];
  index: number;
  sectionTitle: string;
  onClose: () => void;
  onIndexChange: (index: number) => void;
};

const GalleryLightbox = ({
  isOpen,
  media,
  index,
  sectionTitle,
  onClose,
  onIndexChange,
}: GalleryLightboxProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [imageSrc, setImageSrc] = useState("");
  const [isFullResolution, setIsFullResolution] = useState(true);

  const current = media[index];
  const hasMultiple = media.length > 1;

  const goNext = useCallback(() => {
    if (!media.length) return;
    onIndexChange((index + 1) % media.length);
  }, [index, media.length, onIndexChange]);

  const goPrev = useCallback(() => {
    if (!media.length) return;
    onIndexChange((index - 1 + media.length) % media.length);
  }, [index, media.length, onIndexChange]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") goNext();
      if (event.key === "ArrowLeft") goPrev();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, goNext, goPrev]);

  useEffect(() => {
    videoRef.current?.pause();
  }, [index, isOpen]);

  useEffect(() => {
    if (!isOpen || !current || current.type === "video") return;

    const fullSrc = getGalleryLightboxSrc(current);
    const placeholderSrc = current.url;
    let cancelled = false;

    preloadGalleryNeighbors(media, index);

    if (fullSrc === placeholderSrc) {
      setImageSrc(fullSrc);
      setIsFullResolution(true);
      return;
    }

    setImageSrc(placeholderSrc);
    setIsFullResolution(false);

    const loader = new Image();
    loader.decoding = "async";
    loader.onload = () => {
      if (cancelled) return;
      setImageSrc(fullSrc);
      setIsFullResolution(true);
    };
    loader.src = fullSrc;

    return () => {
      cancelled = true;
    };
  }, [isOpen, index, current, media]);

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    setTouchStartX(event.touches[0].clientX);
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX === null || !hasMultiple) return;
    const delta = event.changedTouches[0].clientX - touchStartX;
    if (delta < -SWIPE_THRESHOLD_PX) goNext();
    if (delta > SWIPE_THRESHOLD_PX) goPrev();
    setTouchStartX(null);
  };

  if (!isOpen || !current) return null;

  return createPortal(
    <div
      className="gallery-lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={`${sectionTitle} gallery viewer`}
      onClick={onClose}
    >
      <button
        type="button"
        className="gallery-lightbox-close"
        aria-label="Close gallery viewer"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
      >
        ×
      </button>

      {hasMultiple && (
        <p className="gallery-lightbox-counter" aria-live="polite">
          {index + 1} / {media.length}
        </p>
      )}

      <div
        className="gallery-lightbox-stage"
        onClick={(event) => event.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {hasMultiple && (
          <button
            type="button"
            className="gallery-lightbox-arrow gallery-lightbox-arrow--prev"
            aria-label="Previous"
            onClick={goPrev}
          >
            &#8249;
          </button>
        )}

        <div className="gallery-lightbox-media">
          {current.type === "video" ? (
            <video
              key={current.url}
              ref={videoRef}
              src={current.url}
              controls
              playsInline
              className="gallery-lightbox-video"
              aria-label={`${sectionTitle} — video ${index + 1}`}
            />
          ) : (
            <img
              key={`${index}-${imageSrc}`}
              src={imageSrc}
              alt={`${sectionTitle} — ${index + 1}`}
              className={`gallery-lightbox-image${
                isFullResolution ? "" : " gallery-lightbox-image--preview"
              }`}
              decoding="async"
              fetchPriority="high"
            />
          )}
        </div>

        {hasMultiple && (
          <button
            type="button"
            className="gallery-lightbox-arrow gallery-lightbox-arrow--next"
            aria-label="Next"
            onClick={goNext}
          >
            &#8250;
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default GalleryLightbox;
